import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

/**
 * Minimal portal-based Toast primitive.
 *
 * Uncontrolled state — the caller owns `visible` and flips it on a
 * timer. Renders to `document.body` via a React Portal so it floats
 * above sheets/dialogs regardless of the caller's DOM position.
 *
 * No external toast dependency (Gate-0 F3 decision). If toast use-cases
 * multiply across the PWA, swap for `sonner` at that point; call-sites
 * consuming this primitive stay isolated until then.
 */
interface ToastProps {
  message: string;
  visible: boolean;
  className?: string;
}

export function Toast({ message, visible, className }: ToastProps) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        // Sebastian ruling 2026-11-02 (mobile-audit sweep). Pre-fix
        // `bottom-6` sat at a hard 1.5rem — on iPhone the toast
        // hugged the home indicator (and on landscape could sit
        // directly underneath it). `bottom-[max(1.5rem,calc(env(safe-
        // area-inset-bottom)+0.5rem))]` keeps the pre-fix spacing on
        // devices without a home indicator and grows to clear it on
        // devices that have one.
        'pointer-events-none fixed bottom-[max(1.5rem,calc(env(safe-area-inset-bottom)+0.5rem))] left-1/2 z-[99999] -translate-x-1/2 rounded-full bg-dream-navy px-5 py-2.5 text-sm font-medium text-dream-white shadow-lg transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0',
        className,
      )}
      data-visible={visible ? 'true' : 'false'}
    >
      {message}
    </div>,
    document.body,
  );
}
